import type { Ref } from 'vue'
import type { StationeryPreviewImage } from '~~/layers/base/types/stationeryPreviewImage'
import { useHandwritingProjectService } from '~~/layers/base/services/handwritingProject.service'
import { useUserImageService } from '~~/layers/base/services/userImage.service'
import { debounce } from '~~/layers/base/utils/debounce'
import {
    buildHandwrittenHeroPreviewEntry,
    fingerprintHandwrittenPreviewDataUrl,
    handwrittenPreviewDataUrlToBlob,
} from '~~/layers/base/utils/handwrittenHeroPreviewUpload'
import { mergeDenormalizedPreviewIntoArray } from '~~/layers/base/utils/handwritingProjectDenormalizedPreview'
import { createRequestStartThrottle } from '~~/layers/base/utils/requestStartThrottle'

type HeroPreviewPayload = {
    dataUrl: string | null
    meta: Record<string, unknown>
}

/**
 * Handwritten send-flow hero preview uploader.
 *
 * Owns debounce + request-start throttle + dedupe + user_image create/patch lifecycle for
 * the Fabric hero data URL emitted by the personalize UI.
 */
export function useHandwrittenHeroPreviewUpload(params: {
    isResettingForm: Ref<boolean>
    autosaveEnabled: Ref<boolean>
    selectedHandwritingProjectId: Ref<string | null>
    projectDenormalizedPreviews: Ref<StationeryPreviewImage[]>
}) {
    const {
        isResettingForm,
        autosaveEnabled,
        selectedHandwritingProjectId,
        projectDenormalizedPreviews,
    } = params

    /**
     * Latest hero preview payload from `HandwrittenNotePersonalize` (Fabric `data:` URL + stationery meta).
     * Cleared when the preview is not a data URL (e.g. static hero image only).
     */
    const heroPreviewLatest = ref<{
        dataUrl: string
        meta: Record<string, unknown>
    } | null>(null)

    /**
     * Session-only `user_image.id` for the current project’s hero preview row (first POST, then PATCH).
     * Lost on refresh — a new row is created; see TODOs for stronger continuity / dedupe.
     */
    const currentPreviewUserImageId = ref<string | null>(null)

    /** Fingerprint of the last successfully uploaded preview blob (in-memory dedupe). */
    const lastUploadedPreviewFingerprint = ref<string | null>(null)

    /**
     * Latest upload reference for this session: prefer `cloudflare_image_id`,
     * fall back to the source data URL when an id is unavailable.
     */
    const latestPreviewUploadReference = ref<string | null>(null)
    /**
     * Serializes `user_image` writes (POST/PATCH) and the downstream
     * `handwriting_project.preview_image` PATCH to prevent overlapping mutations.
     * When true, callers queue one follow-up run so the freshest payload wins.
     * Also surfaced to the store as `userImageRequestInFlight` for UI indicators.
     */
    const userImageWriteInFlight = ref(false)
    const rerunAfterInFlightWrite = ref(false)

    const MIN_PREVIEW_UPLOAD_GAP_MS = 2000
    const heroPreviewRequestStartThrottle = createRequestStartThrottle(MIN_PREVIEW_UPLOAD_GAP_MS)

    const { debouncedFn: debouncedHeroPreviewUpload, cancel: cancelHeroPreviewUploadDebounce } =
        debounce(
            () => {
                void tryRunHeroPreviewUpload()
            },
            500,
            'heroPreviewUpload'
        )

    /**
     * Receives composed hero preview from the personalize UI. Debounced before any network work.
     */
    function onHeroPreviewPayload(payload: HeroPreviewPayload) {
        if (!payload.dataUrl?.startsWith('data:')) {
            heroPreviewLatest.value = null
            return
        }
        heroPreviewLatest.value = { dataUrl: payload.dataUrl, meta: payload.meta }
        debouncedHeroPreviewUpload()
    }

    /**
     * Throttle gate + dedupe, then multipart user_image + `preview_image` PATCH.
     *
     * **TODO:** Harden duplicate suppression when multiple tabs or retries race.
     * **TODO:** Delete the previous Cloudflare image after a successful replacement (server + client).
     * **TODO:** Decide whether `user_image.id` should survive refresh (persist id on project vs re-fetch).
     * **TODO:** Evaluate a server-side upsert-by-`handwriting_project_id` if concurrent editors are possible.
     */
    function tryRunHeroPreviewUpload() {
        if (isResettingForm.value) {
            return
        }
        if (!autosaveEnabled.value || !selectedHandwritingProjectId.value) {
            return
        }
        const snap = heroPreviewLatest.value
        if (!snap) {
            return
        }

        const fingerprint = fingerprintHandwrittenPreviewDataUrl(snap.dataUrl)
        if (fingerprint === lastUploadedPreviewFingerprint.value) {
            return
        }
        if (userImageWriteInFlight.value) {
            // Keep only one queued rerun while current POST/PATCH completes.
            rerunAfterInFlightWrite.value = true
            return
        }

        if (
            !heroPreviewRequestStartThrottle.canStartNowOrSchedule(() => {
                void tryRunHeroPreviewUpload()
            })
        ) {
            return
        }

        void executeHeroPreviewUpload(snap, fingerprint)
    }

    async function executeHeroPreviewUpload(
        snap: { dataUrl: string; meta: Record<string, unknown> },
        fingerprint: string
    ) {
        const projectId = selectedHandwritingProjectId.value
        if (!projectId) {
            return
        }

        heroPreviewRequestStartThrottle.markStartedNow()

        const blob = await handwrittenPreviewDataUrlToBlob(snap.dataUrl)
        if (!blob || blob.size === 0) {
            return
        }

        const { doCreateUserImage, doPatchUserImageWithImage } = useUserImageService()
        const { doPatchHandwritingProject } = useHandwritingProjectService()

        userImageWriteInFlight.value = true
        try {
            let cfId: string

            if (!currentPreviewUserImageId.value) {
                const { data, error } = await doCreateUserImage({
                    image: blob,
                    handwriting_project_id: projectId,
                })
                if (error.value || !data.value?.id || !data.value.cloudflare_image_id) {
                    // eslint-disable-next-line no-console
                    console.error('Handwriting preview upload (create) failed:', error.value)
                    return
                }
                currentPreviewUserImageId.value = data.value.id
                cfId = data.value.cloudflare_image_id
            } else {
                const { data, error } = await doPatchUserImageWithImage(
                    currentPreviewUserImageId.value,
                    blob
                )
                if (error.value || !data.value?.cloudflare_image_id) {
                    // eslint-disable-next-line no-console
                    console.error('Handwriting preview upload (patch) failed:', error.value)
                    return
                }
                cfId = data.value.cloudflare_image_id
            }

            const previewEntry = buildHandwrittenHeroPreviewEntry(cfId, snap.meta)
            const merged = mergeDenormalizedPreviewIntoArray(
                projectDenormalizedPreviews.value,
                previewEntry
            )
            projectDenormalizedPreviews.value = merged
            const { error: patchErr } = await doPatchHandwritingProject(projectId, {
                preview_image: merged,
            })
            if (patchErr.value) {
                // eslint-disable-next-line no-console
                console.error('Handwriting preview_image PATCH failed:', patchErr.value)
                return
            }

            lastUploadedPreviewFingerprint.value = fingerprint
            latestPreviewUploadReference.value = cfId || snap.dataUrl
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Handwriting preview upload exception:', e)
        } finally {
            userImageWriteInFlight.value = false
            if (rerunAfterInFlightWrite.value) {
                rerunAfterInFlightWrite.value = false
                void tryRunHeroPreviewUpload()
            }
        }
    }

    /** Cancel pending debounce callback and throttle-delay callback. */
    function cancelPendingHeroPreviewUploadWork() {
        cancelHeroPreviewUploadDebounce()
        heroPreviewRequestStartThrottle.clearWait()
    }

    /**
     * Clear hero preview upload refs + throttle timer. Call after
     * `cancelPendingHeroPreviewUploadWork()` so pending debounce work is already cancelled.
     */
    function resetHeroPreviewUploadSession() {
        heroPreviewRequestStartThrottle.reset()
        heroPreviewLatest.value = null
        currentPreviewUserImageId.value = null
        lastUploadedPreviewFingerprint.value = null
        latestPreviewUploadReference.value = null
        userImageWriteInFlight.value = false
        rerunAfterInFlightWrite.value = false
    }

    return {
        onHeroPreviewPayload,
        cancelPendingHeroPreviewUploadWork,
        resetHeroPreviewUploadSession,
        /** Latest composed hero Fabric `data:` URL + meta (session-only; used on Send store cache). */
        heroPreviewLatest,
        /**
         * True while the user_image POST/PATCH **and** the downstream
         * `handwriting_project.preview_image` PATCH are in flight for the hero pipeline.
         */
        userImageRequestInFlight: userImageWriteInFlight,
    }
}
